import type React from 'react';
import type { FieldRenderProps } from 'react-final-form';
import { QC_FAIL, GENDER_UNKNOWN } from '@alfa-bank/site-one-field-fio';
import type { IFioOutput, INameParts } from '@alfa-bank/site-one-field-fio/src/models';
import type { IOnSelectValue as ISelectEvent } from '@alfa-bank/site-one-field-fio/src/components/models';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';
import type { IFieldSettings } from '@alfa-bank/site-form-utils/src/components/form-field/models';

import type { IFioFields, TName } from '../models';
import { eventToThreeFields } from '../config';

export const onOneFieldFioComplete = (
    { firstName, gender, lastName, middleName }: IFioFields,
    setIsGenderShown: React.Dispatch<React.SetStateAction<boolean>>,
    setIsOneField: React.Dispatch<React.SetStateAction<boolean>>
) => {
    return (fioParams: IFioOutput) => {
        const {
            firstName: firstNameVal = '',
            gender: genderVal = GENDER_UNKNOWN,
            lastName: lastNameVal = '',
            patronymic = '',
            qc,
            restWords = '',
        } = fioParams;
        /* убираем тире в конце строки. это необходимо при отображении значений когда валидация не пройдена и фио в
         одно поле распадается на фио в три поля
         */
        lastName.input.onChange(String(lastNameVal).replace(/-$/, ''));
        firstName.input.onChange(String(firstNameVal).replace(/-$/, ''));
        // добавляет пробел в начале строки, если она есть
        const rest = restWords ? ` ${restWords}` : '';

        middleName.input.onChange(String(patronymic + rest).replace(/-$/, ''));

        lastName.input.onBlur();
        firstName.input.onBlur();
        middleName.input.onBlur();

        /** При подстановке данных через FormApi (initialize, change),
         * после расфокуса, происходит очистка поля и повторная валидация.
         * Чтобы избежать этого, проверяем, что данные в полях: lastName, firstName, middleName,
         * соответствуют данным по-умолчанию, и оставляем текущие значения в полях.
         */

        const { input: lastNameInput, meta: lastNameMeta } = lastName;
        const { input: firstNameInput, meta: firstNameMeta } = firstName;
        const { input: middleNameInput, meta: middleNameMeta } = middleName;

        if (lastNameInput.value && lastNameMeta.pristine)
            lastName.input.onChange(lastNameInput.value);
        if (firstNameInput.value && firstNameMeta.pristine)
            firstName.input.onChange(firstNameInput.value);
        if (middleNameInput.value && middleNameMeta.pristine)
            middleName.input.onChange(middleNameInput.value);

        /** если имя не определилось достоверно, распадается на 3 поля ФИО */
        if (qc === QC_FAIL && lastNameVal !== '') {
            setIsOneField(false);

            sendEvent(eventToThreeFields);
        }

        /** если пол не определился, появляется чипс выбора пола */
        if (genderVal === GENDER_UNKNOWN && lastNameVal) {
            setIsGenderShown(true);
        } else if (genderVal !== GENDER_UNKNOWN) {
            gender.input.onChange(genderVal);
        }

        if (qc !== QC_FAIL && firstNameVal && lastNameVal && patronymic) {
            // Закидываем глобальное событие обратно,
            // что последнее поле компонента завершено,
            // и можно перевести фокус дальше
            actions.onComplete(gender);
        }
    };
};

export const onChangeNamePart = ({ firstName, lastName, middleName }: IFioFields) => {
    return (fioParams: INameParts) => {
        const {
            firstName: firstNameVal = '',
            lastName: lastNameVal = '',
            patronymic = '',
            restWords = '',
        } = fioParams;

        lastName.input.onChange(lastNameVal);
        firstName.input.onChange(firstNameVal);
        // добавляет пробел в начале строки, если она есть
        const rest = restWords ? ` ${restWords}` : '';

        middleName.input.onChange(patronymic + rest);

        lastName.input.onBlur();
        firstName.input.onBlur();
        middleName.input.onBlur();
    };
};

export const onChangeGender =
    (gender: FieldRenderProps<string, HTMLElement>) =>
    ({ value }: ISelectEvent) => {
        gender.input.onChange(value);
        gender.input.onBlur();
    };

export const getWarningText = (
    { firstName, lastName, middleName }: IFioFields,
    checkWarningText: string,
    notFullWarningText: string
) => {
    const isFullFio = lastName.input.value && firstName.input.value && middleName.input.value;

    return isFullFio ? checkWarningText : notFullWarningText;
};

export const getSettingsMappedByFieldNames = (
    name: Record<TName, string>,
    settings: Partial<Record<TName, IFieldSettings>>
) => {
    const fields = Object.keys(name) as TName[];

    return fields.reduce((newSettings: Partial<Record<string, IFieldSettings>>, field) => {
        const filedName = name[field];
        newSettings[filedName] = settings[field];

        return newSettings;
    }, {});
};
